import React from "react"
import { useParams } from "react-router-dom"
import cn from "classnames"
import getAppConfig from "~/utils/getAppConfig"

import CatchCard from "~/library/CatchCard"
import CatchModal from "~/library/CatchModal"
import ResourceNotes from "~/library/ResourceNotes"
import { Task } from "~/types/sponsorship/Task"
import { humanize } from "~/utils/helpers"
import { apiSponsorshipTasks } from "~/routes"
import useFetchResource from "~/hooks/useFetchResource"
import LoadingWrapper from "~/library/LoadingWrapper"

interface TaskViewModalProps {
  isOpen: boolean
  onClose: () => void
  taskId: string
}

const TaskViewModal: React.FC<TaskViewModalProps> = ({ isOpen, onClose, taskId }) => {
  const { venueSlug } = getAppConfig()
  const { id: sponsorshipId } = useParams<{ id: string }>()

  const endpoint = apiSponsorshipTasks.show.path({ venueSlug, sponsorshipId, id: taskId! })
  const { data: task, isLoading } = useFetchResource<{ sponsorshipTask: Task }, "sponsorshipTask">(
    endpoint,
    "sponsorshipTask",
    {}
  )

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  return (
    <CatchModal isOpen={isOpen} onClose={onClose} title="Task Details" size="lg">
      <LoadingWrapper isLoading={isLoading}>
        <CatchCard
          title="Task Information"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
        >
          <div className="row">
            {renderDetail("Title", task.title, 8)}
            {renderDetail("Status", humanize(task.status), 4)}
            {renderDetail(
              "Description",
              task.description ? (
                <p className="mb-0">{task.description}</p>
              ) : (
                <span className="text-muted fst-italic">No description provided</span>
              ),
              12
            )}
          </div>
        </CatchCard>

        {task.media && task.media.length > 0 && (
          <CatchCard title="Attachments" style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}>
            <div className="row">
              {task.media.map((media) => (
                <div key={media.id} className="col-md-12 col-lg-6 mb-3">
                  <div className={cn("d-flex", media.type === "pdf" ? "flex-row" : "flex-column")}>
                    <div className={cn(media.type === "pdf" ? "me-2" : "mb-1")}>
                      {media.type === "image" && (
                        <a href={media.url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={media.url}
                            alt={media.name}
                            className="img-thumbnail"
                            style={{ width: "100%", objectFit: "cover", borderRadius: "6px" }}
                          />
                        </a>
                      )}
                      {media.type === "video" && (
                        <video
                          src={media.url}
                          controls
                          className="img-thumbnail"
                          style={{ width: "100%", objectFit: "cover", borderRadius: "6px" }}
                        />
                      )}
                      {media.type === "pdf" && (
                        <div
                          className="bg-danger text-white d-flex align-items-center justify-content-center img-thumbnail"
                          style={{ width: "50px", height: "50px" }}
                        >
                          PDF
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      {media.type !== "image" && media.type !== "video" ? (
                        <a href={media.url} target="_blank" rel="noopener noreferrer">
                          <p className="mb-1 font-12 fw-bold">{media.name}</p>
                        </a>
                      ) : (
                        <p className="mb-1 font-12 fw-bold">{media.name}</p>
                      )}
                      <p className="mb-0 font-10 text-muted">
                        {media.type?.toUpperCase()} • {Math.round((media.size || 0) / 1024)} KB
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CatchCard>
        )}

        <div className="col-12">
          <CatchCard title="Notes" style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}>
            <ResourceNotes resourceType="sponsorship_task" resourceId={task.id} />
          </CatchCard>
        </div>
      </LoadingWrapper>
    </CatchModal>
  )
}

export default TaskViewModal
